import {
  Component,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputTextModule } from 'primeng/inputtext';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { PDFDocumentProxy, PdfViewerComponent as NgPdfViewerComponent, PdfViewerModule } from 'ng2-pdf-viewer';
import { DocumentService } from '../../services/document.service';
import { PdfViewerService } from '../../services/pdf-viewer.service';

export interface PdfViewerData {
  objectStoreRef?: string;
  blobUrl?: string;
  filename: string;
  title?: string;
}

@Component({
  selector: 'app-pdf-viewer',
  standalone: true,
  imports: [
    FormsModule,
    DialogModule,
    ButtonModule,
    ProgressSpinnerModule,
    InputTextModule,
    ToolbarModule,
    TooltipModule,
    PdfViewerModule,
  ],
  templateUrl: './pdf-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pdf-viewer.component.scss'],
})
export class PdfViewerComponent implements OnInit, OnDestroy {
  @ViewChild(NgPdfViewerComponent, { static: false }) pdfComponent?: NgPdfViewerComponent;
  @ViewChild('pdfContainer', { static: false }) pdfContainer?: ElementRef<HTMLDivElement>;

  private documentService = inject(DocumentService);
  private pdfViewerService = inject(PdfViewerService);

  // Expose Math for template
  Math = Math;

  // Loading and error states
  loading = signal(false);
  initializing = signal(false); // For initial PDF setup
  error = signal<string | null>(null);
  pdfSrc = signal<string | null>(null);

  // PDF viewer state
  currentPage = signal(1);
  totalPages = signal(0);
  zoomLevel = signal(1); // 1 = 100%

  // Sidebar state
  showSidebar = signal(true);
  thumbnails = signal<Array<{ page: number; url: string }>>([]);
  generatingThumbnails = signal(false);

  // Store PDF document reference
  private pdfDocument: PDFDocumentProxy | null = null;

  // Form inputs (not signals for ngModel compatibility)
  searchQuery = '';
  pageInput = 1;

  // Search state
  searchResultsCount = signal(0);
  currentSearchMatch = signal(0);
  private searchDebounceTimer: NodeJS.Timeout | null = null;

  // Event handlers for cleanup
  private keyboardHandler: ((event: KeyboardEvent) => void) | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ng2-pdf-viewer eventBus emits untyped data
  private searchUpdateHandler: ((data: any) => void) | null = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ng2-pdf-viewer eventBus emits untyped data
  private searchControlHandler: ((data: any) => void) | null = null;

  // Local visible state that can be updated by the dialog
  localVisible = signal(false);

  private currentBlobUrl: string | null = null;

  // Get reactive data from service
  data = this.pdfViewerService.document;
  constructor() {
    // Sync service visible state to local state
    effect(() => {
      const serviceVisible = this.pdfViewerService.visible();
      if (serviceVisible && !this.localVisible()) {
        this.localVisible.set(true);
      }
    });
  }

  ngOnInit() {
    // Component initialization
    // Add keyboard shortcut listener for search
    this.setupKeyboardShortcuts();
  }

  private setupKeyboardShortcuts() {
    // Listen for keyboard shortcuts
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if the dialog is visible
      if (!this.localVisible()) return;

      // Cmd+F (Mac) or Ctrl+F (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'f') {
        event.preventDefault();
        event.stopPropagation();

        // Focus the search input
        setTimeout(() => {
          const searchInput = document.querySelector('.search-input') as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
            searchInput.select();
          }
        }, 0);
      }

      // ESC key - clear search if search input is focused
      if (event.key === 'Escape') {
        const searchInput = document.querySelector('.search-input') as HTMLInputElement;
        if (searchInput && document.activeElement === searchInput) {
          event.stopPropagation();
          this.searchQuery = '';
          searchInput.blur();
          this.clearSearch();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Store reference for cleanup
    this.keyboardHandler = handleKeyDown;
  }

  ngOnDestroy() {
    this.cleanup();

    // Clear search debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    // Remove keyboard event listener
    if (this.keyboardHandler) {
      document.removeEventListener('keydown', this.keyboardHandler);
    }

    // Remove search listeners
    if (this.pdfComponent && this.searchUpdateHandler && this.searchControlHandler) {
      this.pdfComponent.eventBus.off('updatefindmatchescount', this.searchUpdateHandler);
      this.pdfComponent.eventBus.off('updatefindcontrolstate', this.searchControlHandler);
    }
  }

  onShow() {
    // Called when dialog is shown - load the document
    this.loadDocument();
  }

  onVisibilityChange(visible: boolean) {
    // Handle visibility changes from the dialog (ESC, close button, etc.)
    this.localVisible.set(visible);
    if (!visible) {
      this.onClose();
    }
  }

  onClose() {
    // This is called when the dialog hides (ESC, X button, etc.)
    // Clean up resources and update service state
    this.localVisible.set(false);
    this.cleanup();
    this.pdfViewerService.closeDocument();
  }

  loadDocument() {
    const currentData = this.data();
    if (!currentData?.objectStoreRef && !currentData?.blobUrl) {
      this.error.set('No document reference provided');
      return;
    }

    // Clear PDF.js stored preferences to ensure zoom attribute is applied
    try {
      localStorage.removeItem('pdfjs.preferences');
      sessionStorage.removeItem('pdfjs.preferences');
    } catch (_e) {
      // Ignore storage access errors
    }

    // If we have a direct blob URL, use it immediately
    if (currentData.blobUrl) {
      // Clean up previous blob URL if it was created by us
      if (this.currentBlobUrl) {
        this.documentService.revokeBlobUrl(this.currentBlobUrl);
      }
      this.currentBlobUrl = currentData.blobUrl;
      this.pdfSrc.set(currentData.blobUrl);
      this.initializing.set(true);
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.documentService.getDocumentBlob(currentData.objectStoreRef!).subscribe({
      next: (blob) => {
        if (blob.type !== 'application/pdf') {
          this.error.set('Document is not a PDF file');
          this.loading.set(false);
          return;
        }

        // Clean up previous blob URL
        if (this.currentBlobUrl) {
          this.documentService.revokeBlobUrl(this.currentBlobUrl);
        }

        // Create new blob URL
        this.currentBlobUrl = this.documentService.createBlobUrl(blob);
        this.pdfSrc.set(this.currentBlobUrl);
        this.loading.set(false);
        // Show initializing spinner immediately when PDF source is set
        this.initializing.set(true);
      },
      error: (err) => {
        console.error('Failed to load document:', err);
        let errorMessage = 'Failed to load document';

        if (err.status === 401) {
          errorMessage = 'You are not authorized to view this document';
        } else if (err.status === 404) {
          errorMessage = 'Document not found';
        } else if (err.status >= 500) {
          errorMessage = 'Server error occurred while loading document';
        }

        this.error.set(errorMessage);
        this.loading.set(false);
      },
    });
  }

  downloadDocument() {
    const currentData = this.data();
    if (currentData?.objectStoreRef && currentData?.filename) {
      this.documentService.downloadDocument(currentData.objectStoreRef, currentData.filename);
    }
  }

  // PDF Viewer Event Handlers
  async onPdfLoaded(pdf: PDFDocumentProxy) {
    this.totalPages.set(pdf.numPages);
    this.currentPage.set(1);
    this.pageInput = 1;

    // Store PDF document and generate thumbnails
    this.pdfDocument = pdf;

    // Start generating thumbnails asynchronously
    this.generateThumbnails(pdf);

    // Set initial zoom to fit page after a delay to ensure container is rendered
    setTimeout(() => {
      this.fitToPage();
      // Hide initializing spinner after setup is complete
      this.initializing.set(false);
    }, 100);
  }

  // Generate thumbnails for all pages
  async generateThumbnails(pdf: PDFDocumentProxy) {
    this.generatingThumbnails.set(true);
    const thumbnailList: Array<{ page: number; url: string }> = [];

    try {
      // Generate thumbnails for up to first 30 pages (balanced for quality and performance)
      const maxThumbnails = Math.min(pdf.numPages, 30);

      for (let pageNum = 1; pageNum <= maxThumbnails; pageNum++) {
        const page = await pdf.getPage(pageNum);
        // Higher scale for better quality thumbnails
        const desiredWidth = 180; // Target width for thumbnails
        const viewport = page.getViewport({ scale: 1 });
        const scale = desiredWidth / viewport.width;
        const scaledViewport = page.getViewport({ scale: scale * 2 }); // 2x for retina quality

        // Create canvas for thumbnail
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (!context) continue;

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;

        // Set canvas style for better rendering
        canvas.style.width = `${scaledViewport.width / 2}px`;
        canvas.style.height = `${scaledViewport.height / 2}px`;

        // Enable image smoothing for better quality
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';

        // Render page to canvas
        await page.render({
          canvasContext: context,
          viewport: scaledViewport,
          intent: 'display', // Better quality rendering
        }).promise;

        // Convert canvas to data URL with higher quality
        const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.9);
        thumbnailList.push({ page: pageNum, url: thumbnailUrl });
      }

      // If there are more pages, add placeholders
      if (pdf.numPages > maxThumbnails) {
        for (let pageNum = maxThumbnails + 1; pageNum <= pdf.numPages; pageNum++) {
          thumbnailList.push({ page: pageNum, url: '' });
        }
      }

      this.thumbnails.set(thumbnailList);
    } catch (error) {
      console.error('Error generating thumbnails:', error);
    } finally {
      this.generatingThumbnails.set(false);
    }
  }

  // Navigate to page from thumbnail
  goToPageFromThumbnail(page: number) {
    this.pageInput = page;
    this.currentPage.set(page);
    this.scrollToPage(page);
  }

  // Toggle sidebar visibility
  toggleSidebar() {
    this.showSidebar.update((show) => !show);
    // Recalculate zoom after sidebar toggle since container size changes
    setTimeout(() => {
      // Always recalculate to maintain proper fit when container resizes
      this.fitToWidth();
    }, 50);
  }

  // Scroll to specific page
  scrollToPage(pageNum: number) {
    if (!this.pdfContainer) return;

    // Find the page element and scroll to it
    const pageElement = this.pdfContainer.nativeElement.querySelector(`.page[data-page-number="${pageNum}"]`);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  // Handle scroll events to update current page
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onScroll(_event: any) {
    if (!this.pdfContainer) return;

    const container = this.pdfContainer.nativeElement;
    const pages = container.querySelectorAll('.page');

    // Find which page is currently most visible
    let currentVisiblePage = 1;
    for (let i = 0; i < pages.length; i++) {
      const page = pages[i] as HTMLElement;
      const rect = page.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Check if this page is in the viewport
      if (
        rect.top <= containerRect.top + containerRect.height / 2 &&
        rect.bottom >= containerRect.top + containerRect.height / 2
      ) {
        currentVisiblePage = i + 1;
        break;
      }
    }

    // Update current page if changed
    if (currentVisiblePage !== this.currentPage()) {
      this.currentPage.set(currentVisiblePage);
      this.pageInput = currentVisiblePage;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPageRendered(_event: any) {
    // Page rendered successfully
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPdfError(error: any) {
    console.error('PDF loading error:', error);
    this.error.set('Failed to load PDF document');
  }

  // Navigation Methods
  previousPage() {
    if (this.pageInput > 1) {
      this.pageInput--;
      this.currentPage.set(this.pageInput);
      this.scrollToPage(this.pageInput);
    }
  }

  nextPage() {
    if (this.pageInput < this.totalPages()) {
      this.pageInput++;
      this.currentPage.set(this.pageInput);
      this.scrollToPage(this.pageInput);
    }
  }

  goToPage() {
    const page = Number(this.pageInput);
    if (!isNaN(page) && page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.pageInput = page;
      this.scrollToPage(page);
    } else {
      // Reset to valid page if invalid
      this.pageInput = this.currentPage();
    }
  }

  onPageChange(page: number) {
    this.currentPage.set(page);
    this.pageInput = page;
  }

  // Zoom Methods
  zoomIn() {
    this.zoomLevel.update((z) => Math.min(z + 0.25, 3)); // Max 300%
  }

  zoomOut() {
    this.zoomLevel.update((z) => Math.max(z - 0.25, 0.5)); // Min 50%
  }

  async fitToWidth() {
    if (!this.pdfDocument || !this.pdfContainer) {
      return;
    }

    try {
      // Get the first page to determine dimensions
      const page = await this.pdfDocument.getPage(1);
      const viewport = page.getViewport({ scale: 1 });

      // Get container dimensions
      const containerEl = this.pdfContainer.nativeElement;
      const containerWidth = containerEl.clientWidth;

      // Calculate zoom with padding and margin to avoid scrollbars
      const padding = 32; // 1rem padding on each side from CSS
      const margin = 50; // Additional margin to prevent scrollbars
      const availableWidth = containerWidth - padding - margin;

      // PDF.js returns dimensions at 72 DPI, but browsers render at 96 DPI
      // Conversion factor: 96/72 = 1.333...
      const DPI_RATIO = 96 / 72;
      const actualPageWidth = viewport.width * DPI_RATIO;

      console.log(
        'Container width:',
        containerWidth,
        'Available width:',
        availableWidth,
        'Viewport width (72 DPI):',
        viewport.width,
        'Actual page width (96 DPI):',
        actualPageWidth
      );

      // Calculate zoom level using actual page width
      const zoom = availableWidth / actualPageWidth;

      // Apply zoom (limit between 0.5 and 3)
      this.zoomLevel.set(Math.max(0.5, Math.min(3, zoom)));
    } catch (error) {
      console.error('Error calculating fit to width:', error);
      // Fallback to a reasonable default
      this.zoomLevel.set(1.2);
    }
  }

  async fitToPage() {
    if (!this.pdfDocument || !this.pdfContainer) {
      return;
    }

    try {
      // Get the first page to determine dimensions
      const page = await this.pdfDocument.getPage(1);
      const viewport = page.getViewport({ scale: 1 });

      // Get container dimensions
      const containerEl = this.pdfContainer.nativeElement;
      const containerWidth = containerEl.clientWidth;
      const containerHeight = containerEl.clientHeight;

      // Calculate zoom with padding and margin to avoid scrollbars
      const padding = 32; // 1rem padding on each side
      const margin = 50; // Additional margin to prevent scrollbars
      const availableWidth = containerWidth - padding - margin;
      const availableHeight = containerHeight - padding - margin;

      // PDF.js returns dimensions at 72 DPI, but browsers render at 96 DPI
      const DPI_RATIO = 96 / 72;
      const actualPageWidth = viewport.width * DPI_RATIO;
      const actualPageHeight = viewport.height * DPI_RATIO;

      // Calculate zoom level for both dimensions and take the smaller
      const zoomWidth = availableWidth / actualPageWidth;
      const zoomHeight = availableHeight / actualPageHeight;
      const zoom = Math.min(zoomWidth, zoomHeight);

      // Apply zoom (limit between 0.5 and 3)
      this.zoomLevel.set(Math.max(0.5, Math.min(3, zoom)));
    } catch (error) {
      console.error('Error calculating fit to page:', error);
      // Fallback to a reasonable default
      this.zoomLevel.set(1.0);
    }
  }

  // Handle search input changes with auto-search
  onSearchChange() {
    // Clear any existing debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }

    const query = this.searchQuery?.trim();

    // If query is less than 3 characters, clear search
    if (!query || query.length < 3) {
      this.clearSearch();
      return;
    }

    // Debounce the search to avoid too many searches while typing
    this.searchDebounceTimer = setTimeout(() => {
      this.search();
    }, 300); // 300ms delay
  }

  // Clear search results
  private clearSearch() {
    if (this.pdfComponent) {
      this.pdfComponent.eventBus.dispatch('find', {
        query: '',
        type: 'clear',
      });
    }
    this.searchResultsCount.set(0);
    this.currentSearchMatch.set(0);
  }

  // Search Methods
  search() {
    const query = this.searchQuery?.trim();
    if (!query || !this.pdfComponent || query.length < 3) {
      this.clearSearch();
      return;
    }

    // Reset match counter for new search
    this.currentSearchMatch.set(1);

    // Listen for search results
    this.setupSearchResultsListener();

    // Use ng2-pdf-viewer's eventBus for search
    this.pdfComponent.eventBus.dispatch('find', {
      query: query,
      type: 'again',
      caseSensitive: false,
      findPrevious: false,
      highlightAll: true,
      phraseSearch: true,
    });
  }

  private setupSearchResultsListener() {
    if (!this.pdfComponent) return;

    const eventBus = this.pdfComponent.eventBus;

    // Remove existing listener if any
    if (this.searchUpdateHandler && this.searchControlHandler) {
      eventBus.off('updatefindmatchescount', this.searchUpdateHandler);
      eventBus.off('updatefindcontrolstate', this.searchControlHandler);
    }

    // Listen for match count updates
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const matchesHandler = (data: any) => {
      if (data && typeof data.matchesCount !== 'undefined') {
        this.searchResultsCount.set(data.matchesCount.total || 0);
      }
    };

    // Listen for current match updates
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const controlHandler = (data: any) => {
      if (data && typeof data.matchIdx !== 'undefined' && data.matchIdx >= 0) {
        this.currentSearchMatch.set(data.matchIdx + 1); // Convert 0-based to 1-based
      }
    };

    eventBus.on('updatefindmatchescount', matchesHandler);
    eventBus.on('updatefindcontrolstate', controlHandler);

    // Store handlers for cleanup
    this.searchUpdateHandler = matchesHandler;
    this.searchControlHandler = controlHandler;
  }

  findNext() {
    const query = this.searchQuery?.trim();
    if (!query || !this.pdfComponent) {
      return;
    }

    // Increment current match (will wrap around)
    const current = this.currentSearchMatch();
    const total = this.searchResultsCount();
    if (total > 0) {
      this.currentSearchMatch.set(current >= total ? 1 : current + 1);
    }

    this.pdfComponent.eventBus.dispatch('find', {
      query: query,
      type: 'again',
      caseSensitive: false,
      findPrevious: false,
      highlightAll: true,
      phraseSearch: true,
    });
  }

  findPrevious() {
    const query = this.searchQuery?.trim();
    if (!query || !this.pdfComponent) {
      return;
    }

    // Decrement current match (will wrap around)
    const current = this.currentSearchMatch();
    const total = this.searchResultsCount();
    if (total > 0) {
      this.currentSearchMatch.set(current <= 1 ? total : current - 1);
    }

    this.pdfComponent.eventBus.dispatch('find', {
      query: query,
      type: 'again',
      caseSensitive: false,
      findPrevious: true,
      highlightAll: true,
      phraseSearch: true,
    });
  }

  private cleanup() {
    if (this.currentBlobUrl) {
      this.documentService.revokeBlobUrl(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }
    this.pdfSrc.set(null);
    this.error.set(null);
    this.currentPage.set(1);
    this.totalPages.set(0);
    this.zoomLevel.set(1);
    this.searchQuery = '';
    this.pageInput = 1;
    this.searchResultsCount.set(0);
    this.currentSearchMatch.set(0);
    // Cleanup thumbnails
    this.thumbnails().forEach((thumb) => URL.revokeObjectURL(thumb.url));
    this.thumbnails.set([]);

    this.pdfDocument = null;
    this.showSidebar.set(true);
    this.initializing.set(false);
  }
}
